

import axios from "axios"
import { useEffect, useState } from "react"
import { FaSearch } from "react-icons/fa"
import { apis } from "../../api/apiUrl"
import { FaAngleLeft } from "react-icons/fa6"
import LecturerDetail from "./lecturer-detail"

const OurPeopleDialogue = ({ id, title }) => {
  const [search, setSearch] = useState("")
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedLecturer, setSelectedLecturer] = useState(null)
  const [lecturerDetails, setLecturerDetails] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const pageSize = 30

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setCurrentPage(1) // Reset to first page on new search
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const handleCardClick = async (lecturer) => {
    setSelectedLecturer(lecturer)
    setDetailsLoading(true)
    setError(null)

    try {
      const response = await axios.get(`${apis.uniten_api}/GetLecturerDetails/?lecturerId=${lecturer.lecturerId}`)
      setLecturerDetails(response.data)
    } catch (err) {
      console.error(err)
      setError("Failed to fetch lecturer details. Please try again.")
    } finally {
      setDetailsLoading(false)
    }
  }

  const handleBackToList = () => {
    setSelectedLecturer(null)
    setLecturerDetails(null)
    setError(null)
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await axios.post(`${apis.uniten_api}/StaffByCollege`, {
          name: search,
          CollegeId: id,
          LecturerId: null,
          currentPage: currentPage,
          pageSize: pageSize,
        })
        setData(response.data.data || [])
        setTotalPages(response.data.totalPages || 1)
      } catch (err) {
        console.error(err)
        setError("Failed to fetch data. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    // Debounced fetch
    const timer = setTimeout(
      () => {
        fetchData()
      },
      search ? 1000 : 0,
    )

    return () => clearTimeout(timer)
  }, [search, id, currentPage])

  // Show lecturer details if one is selected
  if (selectedLecturer) {
    return (
      <LecturerDetail
        lecturer={selectedLecturer}
        details={lecturerDetails}
        loading={detailsLoading}
        error={error}
        onBack={handleBackToList}
      />
    )
  }

  return (
    <div className="flex flex-col gap-5 min-w-[60vw] max-w-full w-full">
      <div className="w-full flex flex-col items-center justify-center gap-8 p-4 pt-10 lg:p-14 bg-[#3d4a6c]">
        <h1 className="text-[clamp(.9rem,5vw,2.5rem)] text-white font-bold">{title}</h1>
        <div className="relative w-full bg-white flex items-center gap-3 text-black rounded-xl overflow-hidden">
          <FaSearch className="ml-8 w-5 h-5" />
          <input
            type="text"
            placeholder="Search Name"
            value={search}
            onChange={handleSearch}
            className="w-full h-full p-3 lg:p-5 font-medium placeholder:font-medium text-black placeholder:text-gray-700 outline-none border-none"
          />
        </div>
      </div>

      <div className="w-full self-center p-4 lg:p-8 overflow-y-auto scroll-bar">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-t-[4px] border-t-[#3d4a6c] rounded-full animate-spin"></div>
            <span className="text-[#3d4a6c] font-bold">Loading...</span>
          </div>
        ) : error ? (
          <div className="w-full h-full flex items-center justify-center gap-3">
            <span className="text-red-500 font-bold">{error}</span>
          </div>
        ) : data?.length > 0 ? (
          <>
            <div className="grid grid-cols-[repeat(auto-fill,_minmax(15rem,_1fr))] w-full place-items-center gap-8">
              {data.map((d, i) => (
                <div
                  key={i}
                  onClick={() => handleCardClick(d)}
                  className="w-full h-[25rem] relative border shrink-0 snap-center group select-none bg-slate-500/30 rounded-3xl overflow-hidden bg-cover bg-no-repeat bg-center cursor-pointer hover:scale-105 transition-transform duration-300"
                >
                  <img
                    src={`${apis.domain}${d?.imageUrl}`}
                    alt={d?.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.target.onerror = null; e.target.src = apis.base_url+'media/czebqq0k/empty-avatar.jpg'; }}
                  />
                  <div className="absolute w-full h-full top-0 bg-gradient-to-b from-black/20 to-black/40 z-20 opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                  <div className="absolute bottom-0 w-full h-2/5 2xl:h-1/3 bg-gradient-to-b from-[#6e6e76]/30 to-[#1b1b1d] px-5 p-4">
                    <h1 className="text-white font-bold text-md 2xl:text-lg">{d?.name}</h1>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-4 bg-[#3d4a6c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaAngleLeft />
                </button>
                <span className="text-[#3d4a6c] font-bold">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-4 bg-[#3d4a6c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaAngleLeft className="rotate-180" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center gap-3">
            <span className="text-[#3d4a6c] font-bold">No Data Found</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default OurPeopleDialogue
